import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CompoundButton } from "@fluentui/react-components";
import { openStyleSheetDialog, openStyleSheetWizard, toGridd } from "../state/appStateSlice";
import { reset } from "../state/structureSlice";
import { init, reset as resetBorders } from "../state/bordersSlice";
import { reset as resetStyles } from "../state/cellStylesSlice";
import logo from "../assets/icons/logo-64x64.svg";
import { BackspaceFilled } from "@fluentui/react-icons";
import { batchGroupBy } from "../helpers/batchGroupBy";
import { storeSheets } from "../state/styleSheetSlice";
import TrackedButton from "./common/TrackedButton";
import CommonTooltip from "./common/CommonTooltip";

const Templates = () => {
  const dispatch = useDispatch();
  const templates = useSelector((state) => state.templates);
  const lastMessage = useSelector((state) => state.app.lastMessage);
  const uiScale = useSelector((state) => state.preferences.uiScale);
  const { t } = useTranslation();

  const [selectedTemplate, setSelectedTemplate] = React.useState();

  function apply(te) {
    batchGroupBy.start();
    dispatch(reset({ columns: te.cols, rows: te.rows, cells: te.cells, merged: te.merged }));
    if (te.borders) {
      dispatch(init(te.borders));
    } else {
      dispatch(resetBorders());
    }
    if (te.cellStyles) {
      dispatch(resetStyles(te.cellStyles));
    } else {
      dispatch(resetStyles());
    }
    batchGroupBy.end();
    dispatch(toGridd());
  }

  return (
    <div
      id="container"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        alignContent: "center",
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
      }}
    >
      <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 12, color: "black", margin: 8 }}>Select layout</div>
      <div
        id="template_items"
        style={{
          display: "flex",
          flexFlow: "wrap",
          height: "90%",
          gap: 8,
          padding: 12,
          justifyContent: "center",
          overflow: "scroll",
        }}
      >
        {templates.all.map((te) => (
          <div key={te.id} style={{ alignContent: "center" }}>
            <CommonTooltip content={t("templates.all." + te.id + ".tooltip")} relationship="description">
              {lastMessage?.griddContentExist || lastMessage?.styleContentExist ? (
                <CompoundButton
                  id={te.id}
                  secondaryContent={t("templates.all." + te.id + ".subtitle")}
                  appearance="subtle"
                  style={{ padding: 4 }}
                  shape="rounded"
                  onClick={() => apply(te)}
                >
                  <div style={{ display: "grid", gridTemplateRows: "auto auto" }}>
                    <img
                      style={{ width: 200 * uiScale, height: 100 * uiScale, objectFit: "contain" }}
                      src={te.thumbnail ?? logo}
                    />
                    <div style={{ width: 200 * uiScale, textAlign: "center" }}>
                      {t("templates.all." + te.id + ".name")}
                    </div>
                  </div>
                </CompoundButton>
              ) : (
                <CompoundButton
                  id={te.id}
                  secondaryContent={t("templates.all." + te.id + ".subtitle")}
                  appearance="subtle"
                  style={{ padding: 4, border: selectedTemplate?.id === te.id ? "1px solid blue" : undefined }}
                  shape="rounded"
                  onClick={() => setSelectedTemplate(te)}
                >
                  <div style={{ display: "grid", gridTemplateRows: "auto auto" }}>
                    <img
                      style={{ width: 200 * uiScale, height: 100 * uiScale, objectFit: "contain" }}
                      src={te.thumbnail ?? logo}
                    />
                    <div style={{ width: 200 * uiScale, textAlign: "center" }}>
                      {t("templates.all." + te.id + ".name")}
                    </div>
                  </div>
                </CompoundButton>
              )}
            </CommonTooltip>
          </div>
        ))}
      </div>

      <div style={{ justifySelf: "right", margin: 8 }}>
        {lastMessage?.griddContentExist ? (
          <div>
            <CommonTooltip content={t("templates.back.tooltip")} relationship="description">
              <CompoundButton
                id="back"
                icon={<BackspaceFilled />}
                onClick={() => {
                  dispatch(toGridd());
                }}
                style={{
                  height: 32,
                }}
              >
                {t("templates.back")}
              </CompoundButton>
            </CommonTooltip>
          </div>
        ) : !lastMessage?.styleContentExist ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 8,
            }}
          >
            <CommonTooltip content={t("templates.apply.tooltip")}>
              <TrackedButton
                appearance="subtle"
                disabled={!selectedTemplate}
                onClick={() => {
                  apply(selectedTemplate);
                  dispatch(storeSheets({ to: "presentation" }));
                }}
              >
                {t("templates.apply")}
              </TrackedButton>
            </CommonTooltip>

            <CommonTooltip content={t("templates.next.tooltip")}>
              <TrackedButton
                appearance="primary"
                disabled={!selectedTemplate}
                onClick={() => {
                  apply(selectedTemplate);
                  dispatch(openStyleSheetDialog());
                  dispatch(openStyleSheetWizard());
                }}
              >
                {t("templates.next")}
              </TrackedButton>
            </CommonTooltip>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default Templates;
